import classnames from 'classnames'
import { Component } from 'preact'
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop'
import Modal from '../../components/reactModal'
import { maxImageDimension, maxImageFileSize, supportedMimeTypes } from '../../libs/constants'
import Button from '../ui-components/Button'
import FakeFileUploader from '../ui-components/FakeFileUploader'

interface ImageData {
  source_thumb: string
  source: string
  type: string
  bbcode_url: string
  short_url: string
  short_thumb_url: string
}

export interface ImageUploaderProps {
  width?: number
  height?: number
  onUploaded: (imageData: ImageData) => void
  uploaderRef: (ref: HTMLInputElement) => void
}

export const DIMENTION_ERROR_MESSAGE = `You can’t upload images with more than ${maxImageDimension} pixels in width and height. Please try again with a smaller image.`

interface State {
  src: FileReader['result'] | null
  crop: Crop
  pixelCrop: PixelCrop | null
  errorMessage: string | null
  enableModal: boolean
  uploading: boolean
}

class ImageUploader extends Component<ImageUploaderProps, State> {
  uploader: HTMLInputElement | null = null
  csrf: string | undefined
  constructor(props: ImageUploaderProps) {
    super(props)
    this.state = {
      src: null,
      crop: {
        x: 0,
        y: 0,
        height: 100
      },
      pixelCrop: null,
      errorMessage: null,
      enableModal: true,
      uploading: false
    }

    const element = document.getElementById('csrftoken')
    if (element) {
      // @ts-expect-error
      this.csrf = element.value
    }

    this.saveImage = this.saveImage.bind(this)
  }

  onSelectFile(event: Event) {
    const reader = new FileReader()
    if (event.target === null) {
      return
    }
    const { files } = event.target as HTMLInputElement
    if (files === null || files === undefined || files.length === 0) {
      return
    }
    const [firstFile] = files
    if (!supportedMimeTypes.includes(firstFile.type)) {
      this.setState({ errorMessage: 'Sorry, this file type is not supported.' })
      return
    }
    if (firstFile.size > maxImageFileSize) {
      this.setState({ errorMessage: 'Sorry, the file size is too large' })
      return
    }

    reader.addEventListener('load', () => {
      const uploadedImage = new Image()
      uploadedImage.onload = () => {
        if (uploadedImage.width > maxImageDimension || uploadedImage.height > maxImageDimension) {
          this.setState({ errorMessage: DIMENTION_ERROR_MESSAGE })
          return
        }
        this.setState({ src: reader.result, errorMessage: null })
      }
      if (typeof reader.result === 'string') {
        uploadedImage.src = reader.result
      }
    })

    reader.readAsDataURL(firstFile)
  }

  saveImage() {
    this.setState({
      uploading: true
    })
    const formData = new FormData()
    const file = this.uploader?.files?.[0]
    if (file) {
      formData.append('image', file)
    }
    if (typeof this.csrf === 'string') {
      formData.append('yip_csrf_token', this.csrf)
    }
    const pixelCrop = this.state.pixelCrop
    if (pixelCrop) {
      formData.append('crop_coords[x]', String(Math.round(pixelCrop.x)))
      formData.append('crop_coords[y]', String(Math.round(pixelCrop.y)))
      formData.append('crop_coords[width]', String(Math.round(pixelCrop.width)))
      formData.append('crop_coords[height]', String(Math.round(pixelCrop.height)))
    }

    const xhr = new XMLHttpRequest()
    xhr.addEventListener(
      'readystatechange',
      (event) => {
        if (event.target === null) {
          return
        }
        const xhrEvent = event.target as XMLHttpRequest
        if (xhrEvent.readyState === 4 && xhrEvent.status === 200 && xhrEvent.responseText) {
          if (this.uploader !== null) {
            this.uploader.value = ''
          }
          const response = JSON.parse(xhrEvent.responseText)
          if (!pixelCrop) response.source_thumb = response.source

          this.props.onUploaded(response)
          this.setState({
            src: null,
            uploading: false
          })
        }
      },
      false
    )

    xhr.open('POST', '/attachment/new', true)
    xhr.send(formData)
  }

  get aspect() {
    const { width, height } = this.props
    if (width !== undefined && height !== undefined) {
      return width / height
    }
    return undefined
  }

  componentDidMount() {
    const crop = { ...this.state.crop, aspect: this.aspect }
    this.setState({ crop })
  }

  cancelUpload() {
    this.setState({ src: null })
    if (this.uploader !== null) {
      this.uploader.value = ''
    }
  }

  render() {
    const { props, state } = this
    const isForum = window.inSidedData?.communityId

    return (
      <div className='image-cropper_uploader'>
        {state.errorMessage && <div className='validation-error'>{state.errorMessage}</div>}
        <FakeFileUploader>
          <input
            type='file'
            hidden
            ref={(ref) => {
              this.uploader = ref
              props.uploaderRef(ref)
            }}
            onChange={(event) => this.onSelectFile(event)}
            accept='image/png|image/jpeg|image/gif'
          />
        </FakeFileUploader>
        <Modal isOpen={Boolean(state.src)} onClose={() => this.cancelUpload()}>
          <div className='image-cropper_title'>Crop Image</div>
          <div className='image-cropper'>
            <ReactCrop
              src={state.src}
              crop={state.crop}
              onChange={(crop: Crop, pixelCrop: PixelCrop) => this.setState({ crop, pixelCrop })}
            />
          </div>
          <div className='image-cropper_footer'>
            <div class='image-cropper_footer-buttons-group'>
              <Button
                class={classnames('btn--insided-brand', {
                  btn: !isForum,
                  'btn-primary': !isForum,
                  'is-loading': this.state.uploading
                })}
                type='button'
                onClick={() => this.saveImage()}
              >
                Save
              </Button>
              <Button
                class={classnames('btn--insided-secondary-brand', {
                  btn: !isForum,
                  'btn-default': !isForum,
                  'button-control--secondary': !isForum
                })}
                type='button'
                onClick={() => this.cancelUpload()}
              >
                Cancel
              </Button>
            </div>
            <Button
              class={classnames('btn--insided-secondary-brand', {
                btn: !isForum,
                'btn-default': !isForum,
                'button-control--secondary': !isForum
              })}
              type='button'
              onClick={() => this.uploader?.click()}
            >
              Choose a new image
            </Button>
          </div>
        </Modal>
      </div>
    )
  }
}

export default ImageUploader
